import React, { useEffect, useState } from 'react';
import { getAiSignalHistory } from '../../services/OrdersService';
import { formatNumber } from '../../utils/NumberUtils';

const SIGNAL_BADGE = {
    BUY:  'badge bg-success',
    SELL: 'badge bg-danger',
    HOLD: 'badge bg-secondary'
};

const OUTCOME_BADGE = {
    CORRECT:   { cls: 'badge bg-success', label: 'Acertou' },
    INCORRECT: { cls: 'badge bg-danger',  label: 'Errou' },
    NEUTRAL:   { cls: 'badge bg-secondary', label: 'Neutro' }
};

/**
 * Modal compacto com os últimos N sinais da IA para um símbolo.
 * Acionado pelo botão "📜" na linha do Watchlist/Cockpit.
 *
 * props:
 * - symbol:   símbolo a buscar (ex: 'BTCUSDT'). Se vazio, modal fechado.
 * - onClose:  callback ao fechar
 * - limit:    quantos sinais mostrar (default 5)
 */
function SignalHistoryModal({ symbol, onClose, limit = 5 }) {
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!symbol) return;
        setLoading(true);
        getAiSignalHistory(symbol, limit)
            .then(rows => setSignals(rows || []))
            .catch(() => setSignals([]))
            .finally(() => setLoading(false));
    }, [symbol, limit]);

    if (!symbol) return null;

    function renderOutcome(outcome) {
        if (!outcome) return <span className='badge bg-light text-muted border'>Pendente</span>;
        const d = OUTCOME_BADGE[outcome];
        if (!d) return <span className='badge bg-secondary'>{outcome}</span>;
        return <span className={d.cls}>{d.label}</span>;
    }

    function renderSignal(sig) {
        const cls = SIGNAL_BADGE[sig] || 'badge bg-secondary';
        return <span className={cls}>{sig}</span>;
    }

    return (
        <div className='modal show d-block' tabIndex='-1' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className='modal-dialog modal-dialog-centered modal-lg' onClick={(e) => e.stopPropagation()}>
                <div className='modal-content'>
                    <div className='modal-header'>
                        <h5 className='modal-title'>
                            🤖 Últimos {limit} sinais da IA — <strong>{symbol}</strong>
                        </h5>
                        <button type='button' className='btn-close' onClick={onClose} aria-label='Fechar' />
                    </div>
                    <div className='modal-body p-0'>
                        {loading && (
                            <div className='text-muted py-4 text-center' style={{ fontSize: 14 }}>
                                Carregando...
                            </div>
                        )}
                        {!loading && signals.length === 0 && (
                            <div className='text-muted py-4 text-center' style={{ fontSize: 14 }}>
                                Nenhum sinal registrado para {symbol} ainda.
                            </div>
                        )}
                        {!loading && signals.length > 0 && (
                            <div className='table-responsive'>
                                <table className='table table-sm table-hover mb-0 align-middle'>
                                    <thead className='thead-light'>
                                        <tr>
                                            <th>Quando</th>
                                            <th className='text-center'>Sinal</th>
                                            <th className='text-end'>Confiança</th>
                                            <th className='text-end'>Preço</th>
                                            <th className='text-center'>Resultado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {signals.map((s, i) => {
                                            const conf = s.confidence != null ? Math.round(parseFloat(s.confidence) * 100) : null;
                                            return (
                                                <React.Fragment key={s.id || i}>
                                                    <tr title={s.reasoning || ''} style={{ cursor: s.reasoning ? 'help' : 'default' }}>
                                                        <td className='text-muted' style={{ fontSize: 12 }}>
                                                            {new Date(s.createdAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                                                        </td>
                                                        <td className='text-center'>{renderSignal(s.signal)}</td>
                                                        <td className='text-end'>{conf != null ? `${conf}%` : '—'}</td>
                                                        <td className='text-end' style={{ fontSize: 12 }}>
                                                            {s.priceAtSignal ? formatNumber(parseFloat(s.priceAtSignal)) : '—'}
                                                        </td>
                                                        <td className='text-center'>{renderOutcome(s.outcome)}</td>
                                                    </tr>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <div className='text-muted text-center py-2' style={{ fontSize: 12 }}>
                                    Passe o mouse sobre uma linha para ver o raciocínio da IA.
                                </div>
                            </div>
                        )}
                    </div>
                    <div className='modal-footer'>
                        <button type='button' className='btn btn-sm btn-secondary' onClick={onClose}>Fechar</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SignalHistoryModal;
